import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { CACHE_CONFIG, getValidCacheData } from "@/utils/cacheManager";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { ROAST_ATTR_TAXONOMY } from "@/constants/common";
import type { RootState } from "@/store/store";
import type { CacheItem } from "@/store/cacheReducer";
import { RoastAttrTerms, type ProductAttributeTerm } from "@/types/productAttribute";
import type { ProductAttrsFilter, ProductsQuery } from "@/viewModels/shop/features/useFilter/types";

//#region Type
interface RoastAttributesViewModel {
  roastAttrTerms: ProductAttributeTerm[];
  selectedRoastTerm: ProductAttributeTerm | null;
  isLoadingRoastAttr: boolean;
  updateSelectedRoastAttrTerm: (selectTerm: ProductAttributeTerm | null) => void;
}
//#endregion

// Helper
const sortRoastAttributes = (data: ProductAttributeTerm[]): ProductAttributeTerm[] => {
  // 定義排序順序
  const order: RoastAttrTerms[] = [
    RoastAttrTerms.LIGHT_ROAST,
    RoastAttrTerms.MEDIUM_ROAST,
    RoastAttrTerms.MEDIUM_DARK_ROAST,
  ];

  // 避免直接 mutate 快取資料
  return [...data].sort(
    (a, b) => order.indexOf(a.slug as RoastAttrTerms) - order.indexOf(b.slug as RoastAttrTerms), // 負數結果 a 在前, 正數 b 在前, 0 不變
  );
};

// 將 Filter 中的「焙度」，轉換成在 UI 中使用的 RoastTerm 物件
const mapFilterToRoastTerm = (
  attrsFilter: ProductAttrsFilter,
  roastTerms: ProductAttributeTerm[],
): ProductAttributeTerm | null => {
  if (!attrsFilter?.length || !roastTerms.length) {
    return null;
  }

  const roastAttr = attrsFilter.find((attr) => attr.attribute === ROAST_ATTR_TAXONOMY);
  if (!roastAttr) {
    return null;
  }

  const targetTerm = roastTerms.find((term) => term.slug === roastAttr.slug);
  return targetTerm ?? null;
};

// Hook
const useRoastAttributes = ({
  filter,
  updateFilter,
}: {
  filter: ProductsQuery;
  updateFilter: (updates: ProductsQuery) => void;
}): RoastAttributesViewModel => {
  const appDispatch = useAppDispatch();

  const cacheState = useSelector((store: RootState) => store.cache);
  const cachedRoastTerms = useMemo(
    () => getValidCacheData<ProductAttributeTerm[]>(CACHE_CONFIG.roastTerms.key, cacheState),
    [cacheState],
  );

  const roastTerms: ProductAttributeTerm[] = useMemo(
    () => (cachedRoastTerms ? sortRoastAttributes(cachedRoastTerms) : []),
    [cachedRoastTerms],
  );

  const selectedRoastTerm: ProductAttributeTerm | null = useMemo(
    () => mapFilterToRoastTerm(filter.attributes, roastTerms),
    [filter.attributes, roastTerms],
  );

  const [isLoading, setIsLoading] = useState(false);

  const fetchAttrTerms = async (attributeId: number, signal?: AbortSignal) => {
    try {
      const { data } = await wooStoreApiService.product.attributes.terms.getAll(
        attributeId,
        signal,
      );

      const cacheItem: CacheItem = {
        data,
        expiry: CACHE_CONFIG.roastTerms.expiry,
        lastFetched: Date.now(),
      };
      appDispatch.setCache(CACHE_CONFIG.roastTerms.key, cacheItem);
    } catch (error) {
      if (signal?.aborted) return;
      handleApiError(error, appDispatch, "取得烘焙度屬性選項失敗，請稍後再試一次");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  const fetchAllAttrs = async (signal?: AbortSignal) => {
    setIsLoading(true);

    try {
      const { data } = await wooStoreApiService.product.attributes.getAll(signal);
      const targetAttr = data.find((attr) => attr.taxonomy === ROAST_ATTR_TAXONOMY);

      if (!targetAttr) {
        console.warn("No attribute found with taxonomy 'pa_roast'");
        return;
      }

      await fetchAttrTerms(targetAttr.id, signal);
    } catch (error) {
      if (signal?.aborted) return;
      handleApiError(error, appDispatch, "取得烘焙度屬性失敗，請稍後再試一次");
    } finally {
      if (signal?.aborted) return;
      setIsLoading(false);
    }
  };

  const updateSelectedRoastAttrTerm = (selectedTerm: ProductAttributeTerm | null) => {
    const updatedAttributes = selectedTerm
      ? [{ attribute: ROAST_ATTR_TAXONOMY, slug: selectedTerm.slug }]
      : undefined;

    updateFilter({ attributes: updatedAttributes });
  };

  useEffect(() => {
    if (cachedRoastTerms) return;

    const controller = new AbortController();
    fetchAllAttrs(controller.signal);

    return () => {
      controller.abort();
    };
  }, [cachedRoastTerms]);

  return {
    roastAttrTerms: roastTerms,
    selectedRoastTerm,
    isLoadingRoastAttr: isLoading,
    updateSelectedRoastAttrTerm,
  };
};

export default useRoastAttributes;
